import _ from 'underscore';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import PermissionConstants from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import {FolderSummary} from 'constants/FolderConstants';
import {FinderResult} from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FinderUtil from 'file-browser/FinderUtil';
import FolderDAO from 'file-browser/FolderDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FileState from 'filestate/FileState';
import AppDispatcher from 'lib/Dispatcher';
import MetadataTabState from 'metadata/MetadataTabState';
import NavStore from 'nav/NavStore';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import notifications, {NotificationOptions} from 'notifications';
import {getUniqueDuplicateItems} from 'registry/RegistryUtil';
import writeSubmitMoveUpdatesToGraphQL from 'registry/writeSubmitMoveUpdatesToGraphQL';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';

import {openRegisterFileModal} from './RegisterFileModal';
import * as RegistryValidatorDAO from './RegistryValidatorDAO';
import {RegistryOptions} from './RegistryValidatorDAO';

export function cancelMove(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.REGISTRY_VALIDATOR_CANCEL,
  });
}

/**
 * Get the common registry among the given files.
 */
function getAndValidateRegistryIdForFiles(results: Array<FinderResult>): string | null {
  let toastOptions: NotificationOptions = {
    duration: 0,
    id: 'registry-validation',
  };
  for (const result of results) {
    if (!result.tagSchema) {
      if (results.length === 1) {
        // If registering one file, provide help action to open Metadata tab.
        toastOptions = {
          ...toastOptions,
          actionText: 'Set Schema',

          onActionClick: () => {
            FileBrowserActions.openFile(result.id, {
              onOpen: (fileState: FileState) => {
                fileState.activateTab(MetadataTabState);
              },
            });
          },
        };
      }
      notifications.warn('Files must have a schema set before registering.', toastOptions);
      return null;
    }
  }
  // eslint-disable-next-line no-restricted-syntax
  const registryIds = _.uniq(results.map((result) => result.tagSchema!.registryId));
  if (registryIds.length > 1) {
    notifications.warn('All items for registration must have a schema in the same registry.', toastOptions);
    return null;
  }
  const registryId = registryIds[0];
  if (
    // TODO: Avoid using NavStore. NavStore is populated on initial page load and can take 30+ seconds to
    // populate for customers with many sources. If it is still loading, assume the user has permission
    // to register - the backend will handle the error if not. Consider making this function async and
    // using `getApolloClient().query(...) to look up permissions (e.g. MetadataToggleRegisterButtonQuery).
    !NavStore.isLoading() &&
    !NavStore.hasSourceAuth(registryId, PermissionConstants.APPEND)
  ) {
    notifications.error('You do not have permissions to register this file.', toastOptions);
    return null;
  }
  return registryId;
}

export async function submitMoveWithDataMods(
  results: Array<FinderResult>,
  warningCodes: {[fileId: string]: string},
  duplicateItemApiIds: {[fileId: string]: Array<string>},
  registryOptions: RegistryOptions
): Promise<void> {
  const duplicateIds = getUniqueDuplicateItems(duplicateItemApiIds);
  const entitiesToRegister = results.filter((result) => (duplicateItemApiIds[result.id] ?? []).length === 0);
  const entityCount = results.length;

  await callAsyncWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds: [],
      itemCounts: {entity: entityCount},
      action: InventoryRegistryDataModAction.REGISTER,
      // This is the number of incoming items being merged, _not_ the number of unique duplicates that
      // will actually be modified
      entitiesToMergeCount: entityCount - entitiesToRegister.length,
    }),
    onPerformAction: (dataModsHeader?: DataModificationsHeader) => {
      submitMove(results, warningCodes, registryOptions, dataModsHeader);
    },
    apiIds: [...duplicateIds, ...entitiesToRegister.map(({id}) => id)],
    analyticsParams: {
      module: 'REGISTRY',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.REGISTER,
      // The entities being registered + the number of duplicates being modified
      numModifiedTotalItems: entityCount + duplicateIds.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
  });
}

export async function submitMove(
  results: Array<FinderResult>,
  warningCodes: {[fileId: string]: string},
  {inUserCollection, inOrgCollection, labelingStrategy}: RegistryOptions,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  const sourceId = getAndValidateRegistryIdForFiles(results);
  if (!sourceId) {
    return;
  }
  // eslint-disable-next-line no-restricted-syntax
  const fileIds = _.pluck(results, 'id');
  AppDispatcher.dispatch({
    actionType: EventConstants.REGISTRY_VALIDATOR_MOVE,
  });

  try {
    const response = await RegistryValidatorDAO.submitMove(
      fileIds,
      sourceId,
      warningCodes,
      {
        inUserCollection,
        inOrgCollection,
        labelingStrategy,
      },
      dataModsHeader
    );
    assert(response != null, 'RegistryValidatorDAO.submitMove unexpectedly returned undefined');
    // TODO(T6276): We need to fetch the root folder via source id for moving files in a folder
    const source = await FileBrowserDAO.fetchSource(sourceId);
    assert(source != null, 'FileBrowserDAO.fetchSource unexpectedly returned undefined');
    const folder: FolderSummary | undefined = await FolderDAO.fetch(source.rootFolder.api_identifier, {
      summary: '1',
    });
    AppDispatcher.dispatch({
      actionType: EventConstants.REGISTRY_VALIDATOR_MOVE_COMPLETED,
      fileIds,
    });
    // eslint-disable-next-line no-restricted-syntax
    _.each(results, (result) => {
      FolderDAOCache.invalidateFolderContents(FinderUtil.parentFolderId(result));
    });

    const {changedDisplayLabelsById, changedNamesById, archivedFileIds, bioentityAliasInfosById, tagsById} =
      response;
    FileBrowserActions.bulkRemoveFromWorkspace(archivedFileIds);

    // BNCH-12636
    // GraphQL syncs its bioentity data with some Redux/Flux stores, including fileSummariesById (bioentity
    // summaries) and the InventoryFileStore (bioentity alias infos). When we dispatch
    // BIOENTITY_ALIAS_INFO_LOAD_COMPLETED, the syncer writes the new aliases to existing bioentities in the
    // Apollo cache.
    //
    // However, the other actions below are not synced (in some cases because they only touch backbone
    // models). This means the Apollo cache would end up with inconsistent data for a given bioentity: Up to
    // date aliases, but an out of date folder. This causes integrity checks and assertions to fail.
    //
    // To fix this, the function called below writes all the relevant updates to the Apollo cache, so data
    // will be consistent. This will no longer be necessary when either:
    // - We've written code to automatically sync state changes between all the various backbone models/stores
    //   updated below. Not worth it just for this one case, but we may end up doing it as part of other
    //   GraphQL conversion projects.
    // - We've converted the entire bioentity summary model to GraphQL, and are not using redux/backbone as
    //   data storage for it any more. When we get to this point, we would remove all the Flux/Redux
    //   dispatches.
    writeSubmitMoveUpdatesToGraphQL(response, fileIds, folder!);
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
      bioentityAliasInfosById,
    });
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_TAGS_UPDATED,
      // eslint-disable-next-line no-restricted-syntax
      tagData: _.map(tagsById, (tags, id) => ({id, tags})),
    });
    for (const id of Object.keys(changedNamesById)) {
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_BROWSER_RENAME_FILE,
        id,
        name: changedNamesById[id],
        bioentityAliasInfos: bioentityAliasInfosById[id],
        displayLabel: changedDisplayLabelsById[id],
      });
    }
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_MOVE_FILES_COMPLETED,
      ids: fileIds,
      permissionsById: response.permissionsById,
      folder,
    });

    const movedItemWord = benchlingUtil.pluralize(response.numMoved, 'item');
    const mergedItemWord = benchlingUtil.pluralize(response.numMerged, 'item');
    const wasWere = benchlingUtil.pluralize(response.numMerged, 'was', 'were');
    const itsTheir = benchlingUtil.pluralize(response.numMerged, 'its', 'their');
    let message = `Successfully moved ${response.numMoved} ${movedItemWord} into the registry.`;
    if (response.numMerged > 0) {
      message +=
        ` ${response.numMerged} ${mergedItemWord} ${wasWere}` +
        ` left in the project and ${itsTheir} samples moved.`;
    }
    notifications.success(message);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (errorResponse) {
    notifications.notifyError(errorResponse, {duration: 0});
    AppDispatcher.dispatch({
      actionType: EventConstants.REGISTRY_VALIDATOR_CANCEL,
    });
  }
}

export function checkItems(results: Array<FinderResult>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.REGISTRY_VALIDATOR_CHECK,
  });
  // eslint-disable-next-line no-restricted-syntax
  const fileIds = _.pluck(results, 'id');
  const sourceId = getAndValidateRegistryIdForFiles(results);
  if (sourceId) {
    RegistryValidatorDAO.checkItems(fileIds, sourceId)
      .done((response) => {
        assert(response != null, 'RegistryValidatorDAO.checkItems unexpectedly returned undefined');
        AppDispatcher.dispatch({
          actionType: EventConstants.REGISTRY_VALIDATOR_CHECK_COMPLETED,
          myCollectionErrorsByFileId: response.myCollectionErrors,
          orgCollectionErrorsByFileId: response.orgCollectionErrors,
          files: response.files,
          addedFileIds: response.addedFileIds,
        });
      })
      .fail((response) => {
        notifications.notifyError(response, {duration: 0});
      });
  }
}

export function openRegistryValidatorModal(results: Array<FinderResult>): void {
  if (getAndValidateRegistryIdForFiles(results)) {
    checkItems(results);
    openRegisterFileModal();
  }
}

export function validateFileTags(fileId: string, registryId: string): void {
  RegistryValidatorDAO.validateFileTags(fileId, registryId).done((errors) => {
    AppDispatcher.dispatch({
      actionType: EventConstants.REGISTRY_VALIDATOR_VALIDATE_FILE_TAGS_COMPLETED,
      errors,
      fileId,
    });
  });
}

/**
 * Return validation errors for items that are both reviewable and taggable.
 *
 * NOTE: this is not registry-related; validation handling is the same for registry and review, so we keep
 * this method here for organizational reasons.
 *
 * @param {string} fileId - the apiIdentifier of either an Entry or a StageEntry
 * @return {jqXHR} - promise that resolves / rejects depending on validation.
 */
export async function validateFileTagsForReview(fileId: string): Promise<Array<SchemaValidationErrorRecord>> {
  try {
    const errors = await ReviewDAO.validateFileTags(fileId);
    AppDispatcher.dispatch({
      actionType: EventConstants.REGISTRY_VALIDATOR_VALIDATE_FILE_TAGS_COMPLETED,
      errors,
      fileId,
    });
    return errors;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
    return [];
  }
}

/**
 * Return validation errors for an array of reviewable items
 *
 * NOTE: this is not registry-related; validation handling is the same for registry and review, so we keep
 * this method here for organizational reasons.
 *
 * @param {Array<string>} reviewableIds - the apiIdentifiers of reviewable items
 * @return {Promise} - promise that resolves / rejects depending on validation.
 */
export async function bulkValidateFileTagsForReview(
  reviewableIds: Array<string>
): Promise<{[reviewableId: string]: Array<SchemaValidationErrorRecord>}> {
  try {
    const errorsByReviewableId = await ReviewDAO.bulkValidateFileTags(reviewableIds);
    AppDispatcher.dispatch({
      actionType: EventConstants.REGISTRY_VALIDATOR_BULK_VALIDATE_FILE_TAGS_COMPLETED,
      errorsByFileId: errorsByReviewableId,
    });
    return errorsByReviewableId;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
    return {};
  }
}
